/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {Stack, Box, Button} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage} from 'react-intl'
import LoadingSpinner from '../loading-spinner'

/**
 * Renders a card-style box with optional edit and remove buttons. Used for
 * lists of addresses, payment methods, or any other list-type content.
 * The provided `onRemove` callback triggers a loading spinner internally
 * if given a promise.
 */
const ActionCard = ({
    children,
    onEdit,
    onRemove,
    editBtnRef,
    editBtnLabel,
    removeBtnLabel,
    ...props
}) => {
    const [showLoading, setShowLoading] = useState(false)

    /**
     * Asynchronous function to handle the removal action.
     *
     * This function sets a loading state to true before attempting to execute
     * the provided `onRemove` function, which is expected to return a promise.
     * Once the promise resolves, the loading state is set back to false.
     *
     * @async
     * @function handleRemove
     * @returns {Promise<*>} The result of the `onRemove` function wrapped in a resolved promise.
     *
     * @example
     * handleRemove().then(result => {
     *   console.log('Removal successful:', result);
     * }).catch(error => {
     *   console.error('Error during removal:', error);
     * });
     */
    const handleRemove = async () => {
        setShowLoading(true)
        try {
            return await Promise.resolve(onRemove())
        } finally {
            setShowLoading(false)
        }
    }

    return (
        <Box
            spacing={4}
            p={4}
            position="relative"
            border="1px solid"
            borderColor="borderGray"
            {...props}
        >
            {showLoading && <LoadingSpinner wrapperStyles={{backgroundColor: 'whiteAlpha'}} />}
            <Stack spacing={3}>
                <Box>{children}</Box>
                <Stack direction="row" spacing={32}>
                    {onEdit && (
                        <Button
                            onClick={onEdit}
                            variant="link"
                            ref={editBtnRef}
                            aria-label={editBtnLabel}
                        >
                            <FormattedMessage defaultMessage="Edit" id="action_card.action.edit" />
                        </Button>
                    )}
                    {onRemove && (
                        <Button variant="link" onClick={handleRemove} aria-label={removeBtnLabel}>
                            <FormattedMessage
                                defaultMessage="Remove"
                                id="action_card.action.remove"
                            />
                        </Button>
                    )}
                </Stack>
            </Stack>
        </Box>
    )
}

ActionCard.propTypes = {
    /** Callback fired on edit */
    onEdit: PropTypes.func,

    /** Callback fired on remove click (if promise, will toggle loading spinner) */
    onRemove: PropTypes.func,

    /** Content rendered in card */
    children: PropTypes.node,

    /** Ref for the edit button so that it can be focused on for accessibility */
    editBtnRef: PropTypes.object,

    /** Accessibility label for edit button */
    editBtnLabel: PropTypes.string,

    /** Accessibility label for remove button */
    removeBtnLabel: PropTypes.string
}

export default ActionCard
